import { useEffect, useState } from 'react';

import NavigationBar from 'components/NavigationBar';
import { getGeneratedLeadsForTimerange } from 'helpers/neo4j/neo4jHelper';

import styled from 'styled-components';
import { useAuthState } from 'stores/AuthStore';
import { getAllVaultsForAdmin, getMetaAdAccountsV23, getAdAccountSpendForDateRange } from 'api';

import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faExclamationTriangle,
  faFileInvoiceDollar,
  faEuroSign,
} from '@fortawesome/free-solid-svg-icons';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ─── Layout ──────────────────────────────────────────────────────────────────

const SPACING = 24;

const Container = styled.div`
  padding: 0 ${SPACING}px ${SPACING}px;
  background: ${({ theme }) => theme.background100};
  min-height: calc(100vh - 60px);
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  padding: ${SPACING}px 0 ${SPACING - 8}px;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.boldTextColor};
  margin: 0;
`;

// ─── KPI Cards ────────────────────────────────────────────────────────────────

const KPIRow = styled.div`
  display: flex;
  gap: ${SPACING}px;
  margin-bottom: ${SPACING}px;
`;

const KPICard = styled.div<{ variant: 'green' | 'red' | 'blue' | 'yellow' | 'purple' }>`
  flex: 1;
  background: ${({ variant }) => {
    const map = {
      green: 'linear-gradient(135deg, rgba(21,171,144,0.12), rgba(21,171,144,0.03))',
      red: 'linear-gradient(135deg, rgba(224,90,106,0.12), rgba(224,90,106,0.03))',
      blue: 'linear-gradient(135deg, rgba(99,179,245,0.18), rgba(99,179,245,0.04))',
      yellow: 'linear-gradient(135deg, rgba(255,204,0,0.18), rgba(255,204,0,0.04))',
      purple: 'linear-gradient(135deg, rgba(130,80,200,0.12), rgba(130,80,200,0.03))',
    };
    return map[variant];
  }};
  border: 1px solid
    ${({ variant }) => {
      const map = {
        green: 'rgba(21,171,144,0.25)',
        red: 'rgba(224,90,106,0.2)',
        blue: 'rgba(99,179,245,0.3)',
        yellow: 'rgba(255,204,0,0.35)',
        purple: 'rgba(130,80,200,0.2)',
      };
      return map[variant];
    }};
  border-radius: 10px;
  padding: 20px 24px;
`;

const KPIIconWrapper = styled.div<{ variant: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  background: ${({ variant }) => {
    const map: Record<string, string> = {
      green: 'rgba(21,171,144,0.15)',
      red: 'rgba(224,90,106,0.12)',
      blue: 'rgba(99,179,245,0.18)',
      yellow: 'rgba(255,204,0,0.2)',
      purple: 'rgba(130,80,200,0.12)',
    };
    return map[variant];
  }};
  color: ${({ variant }) => {
    const map: Record<string, string> = {
      green: '#15ab90',
      red: '#e05a6a',
      blue: '#3b9af0',
      yellow: '#b8920a',
      purple: '#8250c8',
    };
    return map[variant];
  }};
  font-size: 0.85rem;
`;

const KPILabel = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.mainTextColor};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  margin-bottom: 4px;
`;

const KPIValue = styled.div`
  font-size: 2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.boldTextColor};
  line-height: 1;
`;

// ─── Section Card ─────────────────────────────────────────────────────────────

const SectionCard = styled.div`
  background: ${({ theme }) => theme.background};
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: ${SPACING}px;
  border: 1px solid ${({ theme }) => theme.background200};
`;

const SectionTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ theme }) => theme.mainTextColor};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 18px;
`;

const ChartWrapper = styled.div`
  height: 240px;
`;

// ─── Table ────────────────────────────────────────────────────────────────────

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const THead = styled.thead``;

const TH = styled.th<{ align?: string }>`
  padding: 10px 14px;
  background: ${({ theme }) => theme.background100};
  border-bottom: 2px solid ${({ theme }) => theme.background300};
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.mainTextColor};
  font-weight: 600;
  white-space: nowrap;
`;

const TR = styled.tr`
  &:hover td {
    background: ${({ theme }) => theme.background100};
  }
`;

const TD = styled.td<{ align?: string }>`
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.background200};
  color: ${({ theme }) => theme.boldTextColor};
  text-align: ${({ align }) => align || 'left'};
  font-size: 0.9rem;
  transition: background 0.1s;
`;

const MonthCell = styled(TD)`
  font-weight: 500;
`;

const Badge = styled.span<{ color: string; bg: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  color: ${({ color }) => color};
  background: ${({ bg }) => bg};
  font-weight: 600;
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const ExpectedAmountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const ExpectedValue = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.mainTextColor};
  font-style: italic;
`;

const ExpectedLabel = styled.span`
  font-size: 0.7rem;
  color: #b0b5c1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProfitText = styled.span<{ positive: boolean }>`
  font-weight: 500;
  color: ${({ positive }) => (positive ? '#15ab90' : '#e05a6a')};
`;

const MarginBadge = styled.span<{ positive: boolean }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ positive }) => (positive ? '#15ab90' : '#e05a6a')};
  background: ${({ positive }) =>
    positive ? 'rgba(21,171,144,0.1)' : 'rgba(224,90,106,0.1)'};
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toISODate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// ─── Pricing ──────────────────────────────────────────────────────────────────

const JANUARY_ALT_PRICING_VAULT = '8NU4Xqx5Zd';

/** Core pricing logic — returns the price for a lead based on month and vault settings. */
const getLeadPrice = (lead: any, vaults: any[], year: number, monthIndex: number): number => {
  if (!vaults) return 0;
  const vault = vaults.find((v: any) => v.objectId === lead.vaultObjectId);
  const invoiceSettings = vault?.settings?.leadInvoiceSettings;
  if (!invoiceSettings) return 0;

  const { leadBillingType } = invoiceSettings;
  const isJanuary2026 = year === 2026 && monthIndex === 0;

  let lowPrice: number;
  let highPrice: number;

  if (isJanuary2026) {
    const useAlt = lead.vaultObjectId === JANUARY_ALT_PRICING_VAULT;
    lowPrice = useAlt ? 75 : 45;
    highPrice = useAlt ? 85 : 65;
  } else {
    const useAlt = invoiceSettings.useAlternativePricing;
    lowPrice = useAlt ? 65 : 50;
    highPrice = useAlt ? 75 : 70;
  }

  if (leadBillingType === 'buyingReason') {
    if (lead.forInvestment) return highPrice;
    if (lead.forPersonalUse) return lowPrice;
  } else if (leadBillingType === 'contactReason') {
    if (lead.hasAskedToScheduleVisit) return highPrice;
    if (lead.hasAskedForMoreInfo) return lowPrice;
  }

  return 0;
};

/** Actual invoiced amount — only counts leads marked as invoiced. */
const calculateLeadPrice = (lead: any, vaults: any[], year: number, monthIndex: number): number => {
  if (!lead.invoiced) return 0;
  return getLeadPrice(lead, vaults, year, monthIndex);
};

/** Expected amount — counts all non-rejected leads regardless of invoiced status. */
const calculateExpectedLeadPrice = (lead: any, vaults: any[], year: number, monthIndex: number): number => {
  if (lead.rejected) return 0;
  return getLeadPrice(lead, vaults, year, monthIndex);
};

// ─── Component ────────────────────────────────────────────────────────────────

const ReportingPage = () => {
  const { AuthState } = useAuthState();
  const { sessionToken, user } = AuthState;

  const [allVaults, setAllVaults] = useState<any>();
  const [allLeadsPerMonth, setAllLeadsPerMonth] = useState<any[]>();
  const [adAccounts, setAdAccounts] = useState<any[]>();
  const [adSpendPerMonth, setAdSpendPerMonth] = useState<Record<string, number>>({});

  useEffect(() => {
    if (user?.vaultList[0]) {
      getAllVaultsForAdmin(user.vaultList[0].objectId, sessionToken).then(
        (result) => {
          setAllVaults(result.data.vaultList);
        }
      );
    }
  }, [user, sessionToken]);

  useEffect(() => {
    const START_YEAR = 2026;
    const START_MONTH = 0; // January

    const now = new Date();
    const months: { year: number; month: number }[] = [];
    let y = START_YEAR;
    let m = START_MONTH;

    while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
      months.push({ year: y, month: m });
      m++;
      if (m > 11) { m = 0; y++; }
    }

    Promise.all(
      months.map(({ year, month }) => {
        const start = new Date(year, month, 1).getTime();
        const end = new Date(year, month + 1, 0, 23, 59, 59, 999).getTime();
        const label = new Date(year, month, 1).toLocaleString('default', {
          month: 'long',
          year: 'numeric',
        });
        return getGeneratedLeadsForTimerange(start, end).then((result: any) => ({
          month: label,
          year,
          monthIndex: month,
          leads: result,
        }));
      })
    ).then((results) => {
      setAllLeadsPerMonth(results);
    });
  }, []);

  useEffect(() => {
    getMetaAdAccountsV23().then((response: any) => {
      setAdAccounts(response.data.data);
    });
  }, []);

  useEffect(() => {
    if (!adAccounts || !allLeadsPerMonth) return;
    Promise.all(
      allLeadsPerMonth.map((monthData) => {
        const startDate = toISODate(new Date(monthData.year, monthData.monthIndex, 1));
        const endDate = toISODate(new Date(monthData.year, monthData.monthIndex + 1, 0));
        return Promise.all(
          adAccounts.map((account: any) =>
            getAdAccountSpendForDateRange(account.id, startDate, endDate)
              .then((res: any) => Number(res.data.data?.[0]?.spend ?? 0))
              .catch(() => 0)
          )
        ).then((spends) => ({
          month: monthData.month,
          spend: spends.reduce((sum: number, s: number) => sum + s, 0),
        }));
      })
    ).then((results) => {
      const spendMap: Record<string, number> = {};
      results.forEach(({ month, spend }) => { spendMap[month] = spend; });
      setAdSpendPerMonth(spendMap);
    });
  }, [adAccounts, allLeadsPerMonth]);

  // ─── Derived stats ──────────────────────────────────────────────────────────

  const totalLeads =
    allLeadsPerMonth?.reduce((sum, m) => sum + m.leads.length, 0) ?? 0;

  const totalDisputed =
    allLeadsPerMonth?.reduce(
      (sum, m) => sum + m.leads.filter((x: any) => x.rejected).length,
      0
    ) ?? 0;

  const totalInvoiced =
    allLeadsPerMonth?.reduce(
      (sum, m) => sum + m.leads.filter((x: any) => x.invoiced).length,
      0
    ) ?? 0;

  const totalInvoicedAmount =
    allLeadsPerMonth?.reduce(
      (sum, m) =>
        sum +
        m.leads.reduce(
          (s: number, l: any) => s + calculateLeadPrice(l, allVaults, m.year, m.monthIndex),
          0
        ),
      0
    ) ?? 0;

  // ─── Chart ──────────────────────────────────────────────────────────────────

  const chartLabels = allLeadsPerMonth?.map((m) => m.month) ?? [];

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Generated',
        data: allLeadsPerMonth?.map((m) => m.leads.length) ?? [],
        backgroundColor: 'rgba(21,171,144,0.7)',
        borderRadius: 4,
      },
      {
        label: 'Disputed',
        data:
          allLeadsPerMonth?.map(
            (m) => m.leads.filter((x: any) => x.rejected).length
          ) ?? [],
        backgroundColor: 'rgba(224,90,106,0.7)',
        borderRadius: 4,
      },
      {
        label: 'Invoiced',
        data:
          allLeadsPerMonth?.map(
            (m) => m.leads.filter((x: any) => x.invoiced).length
          ) ?? [],
        backgroundColor: 'rgba(99,179,245,0.8)',
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          borderRadius: 3,
          useBorderRadius: true,
          font: { size: 12 },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 } },
      },
      y: {
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { font: { size: 12 }, precision: 0 },
        beginAtZero: true,
      },
    },
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Container>
      <NavigationBar />

      <PageHeader>
        <PageTitle>Lead Reporting</PageTitle>
      </PageHeader>

      {/* KPI Cards */}
      <KPIRow>
        <KPICard variant="green">
          <KPIIconWrapper variant="green">
            <FontAwesomeIcon icon={faChartBar as any} />
          </KPIIconWrapper>
          <KPILabel>Total Leads</KPILabel>
          <KPIValue>{totalLeads}</KPIValue>
        </KPICard>

        <KPICard variant="red">
          <KPIIconWrapper variant="red">
            <FontAwesomeIcon icon={faExclamationTriangle as any} />
          </KPIIconWrapper>
          <KPILabel>Disputed</KPILabel>
          <KPIValue>{totalDisputed}</KPIValue>
        </KPICard>

        <KPICard variant="blue">
          <KPIIconWrapper variant="blue">
            <FontAwesomeIcon icon={faFileInvoiceDollar as any} />
          </KPIIconWrapper>
          <KPILabel>Invoiced Leads</KPILabel>
          <KPIValue>{totalInvoiced}</KPIValue>
        </KPICard>

        <KPICard variant="yellow">
          <KPIIconWrapper variant="yellow">
            <FontAwesomeIcon icon={faEuroSign as any} />
          </KPIIconWrapper>
          <KPILabel>Invoiced Amount</KPILabel>
          <KPIValue>€{totalInvoicedAmount.toLocaleString()}</KPIValue>
        </KPICard>
      </KPIRow>

      {/* Chart */}
      <SectionCard>
        <SectionTitle>Monthly Overview</SectionTitle>
        <ChartWrapper>
          <Bar data={chartData} options={chartOptions} />
        </ChartWrapper>
      </SectionCard>

      {/* Table */}
      <SectionCard>
        <SectionTitle>Month-by-Month Breakdown</SectionTitle>
        <Table>
          <THead>
            <tr>
              <TH>Month</TH>
              <TH align="right">Generated</TH>
              <TH align="right">Disputed</TH>
              <TH align="right">Dispute Rate</TH>
              <TH align="right">Invoiced Leads</TH>
              <TH align="right">Invoiced Amount</TH>
              <TH align="right">Ad Spend</TH>
              <TH align="right">Profit</TH>
              <TH align="right">Margin</TH>
            </tr>
          </THead>
          <tbody>
            {allLeadsPerMonth?.map((item: any) => {
              const now = new Date();
              const isCurrentMonth =
                item.year === now.getFullYear() && item.monthIndex === now.getMonth();

              const generated = item.leads.length;
              const disputed = item.leads.filter((x: any) => x.rejected).length;
              const invoiced = item.leads.filter((x: any) => x.invoiced).length;
              const invoicedAmount = item.leads.reduce(
                (sum: number, lead: any) =>
                  sum + calculateLeadPrice(lead, allVaults, item.year, item.monthIndex),
                0
              );
              const expectedAmount = isCurrentMonth
                ? item.leads.reduce(
                    (sum: number, lead: any) =>
                      sum + calculateExpectedLeadPrice(lead, allVaults, item.year, item.monthIndex),
                    0
                  )
                : 0;

              const adSpend = adSpendPerMonth[item.month] ?? 0;
              const revenueForMargin = isCurrentMonth ? expectedAmount : invoicedAmount;
              const profit = revenueForMargin - adSpend;
              const marginPct =
                revenueForMargin > 0 ? Math.round((profit / revenueForMargin) * 100) : 0;

              const disputeRate =
                generated > 0
                  ? Math.round((disputed / generated) * 100)
                  : 0;

              return (
                <TR key={item.month}>
                  <MonthCell>{item.month}</MonthCell>
                  <TD align="right">{generated}</TD>
                  <TD align="right">
                    {disputed > 0 ? (
                      <Badge color="#e05a6a" bg="rgba(224,90,106,0.1)">
                        {disputed}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD align="right">
                    {disputeRate > 0 ? (
                      <Badge
                        color={disputeRate > 20 ? '#e05a6a' : '#b8920a'}
                        bg={
                          disputeRate > 20
                            ? 'rgba(224,90,106,0.1)'
                            : 'rgba(255,204,0,0.15)'
                        }
                      >
                        {disputeRate}%
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD align="right">
                    {invoiced > 0 ? (
                      <Badge color="#15ab90" bg="rgba(21,171,144,0.1)">
                        {invoiced}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </TD>
                  <TD align="right">
                    {isCurrentMonth ? (
                      <ExpectedAmountWrapper>
                        <ExpectedValue>~€{expectedAmount.toLocaleString()}</ExpectedValue>
                        <ExpectedLabel>Expected</ExpectedLabel>
                      </ExpectedAmountWrapper>
                    ) : (
                      <AmountText>€{invoicedAmount.toLocaleString()}</AmountText>
                    )}
                  </TD>
                  <TD align="right">
                    <AmountText>€{adSpend.toLocaleString()}</AmountText>
                  </TD>
                  <TD align="right">
                    {isCurrentMonth ? (
                      <ExpectedAmountWrapper>
                        <ProfitText positive={profit >= 0}>
                          {profit >= 0 ? '+' : ''}~€{profit.toLocaleString()}
                        </ProfitText>
                        <ExpectedLabel>Expected</ExpectedLabel>
                      </ExpectedAmountWrapper>
                    ) : (
                      <ProfitText positive={profit >= 0}>
                        {profit >= 0 ? '+' : ''}€{profit.toLocaleString()}
                      </ProfitText>
                    )}
                  </TD>
                  <TD align="right">
                    {revenueForMargin > 0 ? (
                      <MarginBadge positive={marginPct >= 0}>{marginPct}%</MarginBadge>
                    ) : (
                      '—'
                    )}
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      </SectionCard>
    </Container>
  );
};

export default ReportingPage;
